import { useState, useEffect, useCallback } from 'react';

export interface NetworkStatus {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether the user was recently offline (for showing recovery message) */
  wasOffline: boolean;
  /** Timestamp of last online state */
  lastOnlineAt: Date | null;
}

export interface UseNetworkStatusReturn extends NetworkStatus {
  /** Clear the wasOffline flag (dismiss recovery banner) */
  clearWasOffline: () => void;
}

/**
 * Hook to monitor browser network status.
 *
 * Tracks online/offline state and provides a wasOffline flag
 * for showing "back online" recovery messages.
 *
 * @example
 * ```tsx
 * const { isOnline, wasOffline, clearWasOffline } = useNetworkStatus();
 *
 * if (!isOnline) {
 *   return <OfflineBanner />;
 * }
 *
 * if (wasOffline) {
 *   return <BackOnlineBanner onDismiss={clearWasOffline} />;
 * }
 * ```
 */
export function useNetworkStatus(): UseNetworkStatusReturn {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    wasOffline: false,
    lastOnlineAt: typeof navigator !== 'undefined' && navigator.onLine ? new Date() : null,
  });

  useEffect(() => {
    const handleOnline = () => {
      setStatus((prev) => ({
        isOnline: true,
        // Only set wasOffline if we were actually offline
        wasOffline: !prev.isOnline,
        lastOnlineAt: new Date(),
      }));
    };

    const handleOffline = () => {
      setStatus((prev) => ({
        ...prev,
        isOnline: false,
        // Clear wasOffline when going offline
        wasOffline: false,
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const clearWasOffline = useCallback(() => {
    setStatus((prev) => ({ ...prev, wasOffline: false }));
  }, []);

  return {
    ...status,
    clearWasOffline,
  };
}
